// «Бэкапы и хранилище» (Настройки → Система): последняя копия базы и куда она ушла (сервер, Cloudflare R2),
// учения восстановления и сколько места занимают медиа. Копии делает контейнер backup на сервере: каждую ночь
// в 03:30, учения — по воскресеньям. Куда ушла копия, он записывает сам: ключей R2 у платформы нет.
// После базы в тот же бакет R2 уходят медиа (только новые файлы), репозиторий релизов и зашифрованные настройки
// сервера (.env) — settings['backup.last'].media / .repo / .env (infra/backup/offsite.sh).
import { Card, Chip, int, Meter, plural, when } from '@autoaff/ui';
import { useQuery } from '@tanstack/react-query';
import { Cloud, DatabaseBackup, HardDrive, RotateCcw } from 'lucide-react';
import { api } from '../api';

/** Копия в R2 кроме базы: когда ушла или почему нет (неудача заменяет запись целиком). */
interface OffsiteCopy {
  at?: string;
  key?: string;
  size?: number;
  lastError?: string | null;
  failedAt?: string;
}

export interface BackupState {
  at?: string;
  size?: number;
  offsite?: string | null;
  lastError?: string | null;
  failedAt?: string | null;
  drill?: { at?: string; ok?: boolean; details?: string };
  /** Медиа в R2: за ночь уходят только новые файлы, не больше отведённого времени. */
  media?: {
    at?: string;
    files?: number;
    sent?: number;
    bytes?: number;
    alreadyThere?: number;
    left?: number;
    errors?: number;
    lastError?: string | null;
  };
  /** Репозиторий релизов (зашифрован вашим ключом). */
  repo?: OffsiteCopy;
  /** Настройки сервера (.env, зашифрованы вашим ключом). */
  env?: OffsiteCopy;
}

export interface StorageState {
  /** Последняя копия базы в Cloudflare R2; null — копии только на сервере. */
  offsite: { bucket: string | null; key: string; size: number | null; at: string | null } | null;
  /** Медиа на диске сервера и сам диск. */
  media: { files: number; bytes: number; disk: { free: number; total: number } | null };
}

export interface SystemState {
  backup: BackupState | null;
  /** Нет у api до 26.09 — карточки показывают только бэкап. */
  storage?: StorageState;
}

export function useSystem() {
  return useQuery({
    queryKey: ['system'],
    queryFn: () => api<SystemState>('/api/system'),
    refetchInterval: 5 * 60_000,
  });
}

/** «812 КБ», «46.4 МБ», «5.1 ГБ», «212 ГБ». */
export function fileSize(bytes: number): string {
  if (bytes <= 0) return '0 МБ';
  if (bytes < 1024 ** 2) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} МБ`;
  const gb = bytes / 1024 ** 3;
  return `${gb < 100 ? gb.toFixed(1) : int(gb)} ГБ`;
}

/** Последняя попытка бэкапа (или выгрузки в R2) не удалась — позже последней удачной копии. */
export function backupFailed(b: BackupState | null | undefined): boolean {
  return Boolean(b?.failedAt && (!b.at || Date.parse(b.failedAt) > Date.parse(b.at)));
}

type Row = { key: string; text: string; tone: 'ok' | 'warn' | 'bad' };

/**
 * Строки «что ещё ушло в R2»: медиа, репозиторий релизов, настройки сервера. Ошибки — словами: часть медиа
 * не ушла — предупреждение (догонят следующей ночью), копия релизов или настроек не ушла — ошибка.
 */
function offsiteRows(b: BackupState | null | undefined): Row[] {
  const rows: Row[] = [];
  const m = b?.media;
  if (m?.at) {
    const sent = m.sent ?? 0;
    const parts = [
      sent ? `новых ${int(sent)} (${fileSize(m.bytes ?? 0)})` : 'новых файлов не было',
      m.files ? `всего ${int(m.files)} ${plural(m.files, 'файл', 'файла', 'файлов')}` : null,
      m.left ? `ещё ${int(m.left)} уйдут следующей ночью` : null,
      m.errors ? `не ушло ${int(m.errors)} — повторим следующей ночью` : null,
    ];
    rows.push({
      key: 'media',
      text: `Медиа — ${when(m.at)}: ${parts.filter(Boolean).join(', ')}`,
      tone: m.errors ? 'warn' : 'ok',
    });
  }
  const copy = (key: string, what: string, c: OffsiteCopy | undefined) => {
    if (!c) return;
    if (c.lastError)
      rows.push({
        key,
        text: `${what} — не ушла${c.failedAt ? ` ${when(c.failedAt)}` : ''}: ${c.lastError}. Повторим следующей ночью.`,
        tone: 'bad',
      });
    else if (c.at)
      rows.push({ key, text: `${what} — ${when(c.at)}${c.size ? `, ${fileSize(c.size)}` : ''}`, tone: 'ok' });
  };
  copy('repo', 'Репозиторий релизов', b?.repo);
  copy('env', 'Настройки сервера (зашифрованы)', b?.env);
  return rows;
}

export function BackupCard() {
  const q = useSystem();
  const b = q.data?.backup;
  const s = q.data?.storage;
  const failed = backupFailed(b);
  const o = s?.offsite ?? null;
  const disk = s?.media.disk ?? null;
  const used = disk && disk.total > 0 ? (disk.total - disk.free) / disk.total : null;
  const extra = offsiteRows(b);
  const extraBad = extra.some((r) => r.tone !== 'ok');

  if (q.isPending)
    return (
      <Card title="Бэкапы и хранилище">
        <div className="flex flex-col gap-3">
          <div className="skeleton" style={{ height: 40 }} />
          <div className="skeleton" style={{ height: 40 }} />
          <div className="skeleton" style={{ height: 40 }} />
        </div>
      </Card>
    );
  if (q.isError)
    return (
      <Card title="Бэкапы и хранилище">
        <p className="muted" style={{ margin: 0 }}>
          Не удалось узнать состояние бэкапов. {(q.error as Error).message}
        </p>
      </Card>
    );

  return (
    <Card title="Бэкапы и хранилище">
      <ul className="backup-list">
        <li>
          <DatabaseBackup aria-hidden="true" />
          <div className="min-w-0">
            <b>Копия базы</b>
            <span>
              {!b
                ? 'Первая копия появится после запуска платформы на сервере — дальше каждую ночь в 03:30.'
                : failed
                  ? `Последняя попытка не удалась: ${b.lastError ?? 'ошибка'}.${b.at ? ` Последняя удачная копия — ${when(b.at)}.` : ''}`
                  : b.at
                    ? `${when(b.at)}${b.size ? `, ${fileSize(b.size)}` : ''} · на диске сервера хранятся 7 ежедневных и 4 недельных копии`
                    : 'Ещё не делалась.'}
            </span>
          </div>
          {b &&
            (failed ? (
              <Chip tone="danger">Ошибка</Chip>
            ) : b.at ? (
              <Chip tone="success" dot>
                Есть
              </Chip>
            ) : null)}
        </li>
        <li>
          <Cloud aria-hidden="true" />
          <div className="min-w-0">
            <b>Cloudflare R2</b>
            <span>
              {o
                ? `Зашифрованная копия ушла ${o.at ? when(o.at) : 'после запуска на сервере'}${o.size ? `, ${fileSize(o.size)}` : ''}${o.bucket ? ` · бакет ${o.bucket}` : ''}. Открыть её можно только вашим ключом — у сервера его нет.`
                : 'Копии лежат только на сервере. R2 подключается на сервере: ключ доступа к бакету и ваш открытый ключ шифрования задаются там.'}
            </span>
            {extra.length > 0 && (
              <ul className="backup-sub">
                {extra.map((r) => (
                  <li key={r.key} data-tone={r.tone}>
                    {r.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {o ? (
            extraBad ? (
              <Chip tone="warning">Есть ошибки</Chip>
            ) : (
              <Chip tone="success" dot>
                Подключено
              </Chip>
            )
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </li>
        <li>
          <RotateCcw aria-hidden="true" />
          <div className="min-w-0">
            <b>Учения восстановления</b>
            <span>
              {b?.drill?.at
                ? `${when(b.drill.at)} · ${b.drill.ok ? 'копия развёрнута во временную базу, таблицы сверены' : (b.drill.details ?? 'не пройдены')}`
                : 'Раз в неделю копию разворачиваем во временную базу и сверяем с живой.'}
            </span>
          </div>
          {b?.drill?.at &&
            (b.drill.ok ? (
              <Chip tone="success" dot>
                Пройдены
              </Chip>
            ) : (
              <Chip tone="danger">Не пройдены</Chip>
            ))}
        </li>
        {s && (
          <li>
            <HardDrive aria-hidden="true" />
            <div className="min-w-0">
              <b>Медиа</b>
              <span>
                {s.media.files
                  ? `Ролики, футаж, картинки и озвучка — ${fileSize(s.media.bytes)}, ${int(s.media.files)} ${plural(s.media.files, 'файл', 'файла', 'файлов')}. Лежат на диске сервера${b?.media?.at ? ', копия — в R2 каждую ночь' : ', в R2 пока не копируются'}.`
                  : 'Файлов пока нет. Ролики, футаж и картинки будут лежать на диске сервера.'}
              </span>
              {disk && used !== null && (
                <div className="backup-disk">
                  <div className="flex justify-between gap-3">
                    <span>Диск сервера</span>
                    <span className="tnum">
                      свободно {fileSize(disk.free)} из {fileSize(disk.total)}
                    </span>
                  </div>
                  <Meter
                    value={used}
                    label="Занято на диске сервера"
                    tone={used >= 0.95 ? 'danger' : used >= 0.9 ? 'warn' : undefined}
                  />
                </div>
              )}
            </div>
          </li>
        )}
      </ul>
    </Card>
  );
}
